import { createFileRoute, Link } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import {
  listTestimonialsAdmin,
  reorderTestimonials,
  deleteTestimonial,
} from "@/lib/api/marketing.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { MarketingAdminNav } from "@/components/admin/MarketingAdminNav";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { tableActionsColumn, TableRowActions } from "@/components/admin/TableRowActions";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/marketing/testimonials/")({
  loader: () => listTestimonialsAdmin(),
  component: TestimonialsListPage,
});

type Row = Awaited<ReturnType<typeof listTestimonialsAdmin>>[number];

function TestimonialsListPage() {
  const initial = Route.useLoaderData();
  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);

  async function persistOrder(next: Row[]) {
    setBusy(true);
    try {
      await reorderTestimonials({ data: { ids: next.map((r) => r.id) } });
      setRows(next);
      toast.success("Order updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to reorder");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row: Row) {
    try {
      await deleteTestimonial({ data: { id: row.id } });
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success("Testimonial deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
      throw e;
    }
  }

  const columns: ColumnDef<Row>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    { accessorKey: "role", header: "Role" },
    {
      accessorKey: "quote",
      header: "Quote",
      cell: ({ row }) => (
        <span className="line-clamp-2 max-w-md text-muted-foreground">{row.original.quote}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      ...tableActionsColumn<Row>(),
      cell: ({ row }) => (
        <TableRowActions
          edit={{ to: "/admin/marketing/testimonials/$id", params: { id: row.original.id } }}
          onDelete={() => handleDelete(row.original)}
          deleteLabel={row.original.name}
        />
      ),
    },
  ];

  return (
    <AdminPageShell>
      <AdminPageHeader
        title="Marketing"
        action={
          <Button asChild>
            <Link to="/admin/marketing/testimonials/new">
              <Plus className="mr-2 h-4 w-4" /> Add testimonial
            </Link>
          </Button>
        }
      />
      <MarketingAdminNav />
      <p className="mb-4 text-sm text-muted-foreground">
        Shown on the Careers page. Drag rows to reorder.
      </p>
      <DataTable
        tableId="testimonials"
        columns={columns}
        data={rows}
        enableRowReorder
        onReorder={persistOrder}
        reorderDisabled={busy}
      />
    </AdminPageShell>
  );
}
